import {
  isAppDataLoaded,
  type SalesOrderAmounts,
  type SalesOrderProductAmounts,
} from '@/lib/helpers';
import type { AnyAppData } from '@/server/app-data';
import type { SalesOrderItemAmounts } from '@/server/sales-orders/sales-order-items';
import type { SalesOrderProductColor } from '@/server/sales-orders/sales-order-product-colors-get';
import type { RichSalesOrderProduct } from '@/server/sales-orders/sales-order-products-get';
import type { SalesOrderSummaryAmounts } from '@/server/sales-orders/sales-order-summary';
import {
  type SalesOrder,
  type AdditionalRichData,
  type SalesOrderAssortment,
} from '@/server/sales-orders/sales-orders-get';
import type { SalesOrdersShop } from '@/server/sales-orders/sales-orders-shops';
import type { SalesOrderProductItemOrderType } from '@repo/db';
import { multiply, add, toNumber } from '@repo/utils/calc';
import { subtractVat, defaultVat } from '@repo/utils/vat';

type PartialSalesOrderItem = Record<string, { amount?: number }>;

export function getSizeTotalAmount(values: PartialSalesOrderItem | undefined, batchSize = 1) {
  if (!values || (values && !Object.values(values).length)) {
    return 0;
  }

  let batchAmount = parseInt(batchSize.toString());
  batchAmount = batchAmount > 0 ? batchAmount : 1;

  return (
    Object.values(values).reduce(
      (acc, item) => (acc += parseInt(item.amount?.toString() || '0')),
      0
    ) * batchAmount
  );
}

export function getComponentTotalAmount(
  values: PartialSalesOrderItem | undefined,
  componentAmount: number
) {
  if (!values || (values && !Object.values(values).length)) {
    return 0;
  }
  return (
    Object.values(values).reduce(
      (acc, item) => (acc += parseInt(item.amount?.toString() || '0')),
      0
    ) * componentAmount
  );
}

export function getBatchTotalAmount(values: PartialSalesOrderItem | undefined, batchSize = 1) {
  if (!values || (values && !Object.values(values).length)) {
    return 0;
  }

  let batchAmount = parseInt(batchSize.toString());
  batchAmount = batchAmount > 0 ? batchAmount : 1;

  return (
    Object.values(values).reduce(
      (acc, item) => (acc += parseInt(item.amount?.toString() || '0')),
      0
    ) * parseInt(batchAmount.toString())
  );
}

export function getColorTotalAmount(
  orderType: SalesOrderProductItemOrderType | 'BATCH_ORDER' | '',
  color: SalesOrderProductColor,
  batchSize: number,
  colorAmounts: SalesOrderProductAmounts | SalesOrderItemAmounts | undefined
) {
  const { colorSizes, components } = color;
  let totalAmount = 0;
  if (orderType === 'COMPONENT_ORDER') {
    totalAmount = Object.values(components).reduce((acc, component) => {
      const componentValues =
        typeof colorAmounts === 'undefined'
          ? color.salesOrderItems?.[`comp${component.componentId}`]
          : typeof colorAmounts[component.componentId] !== 'undefined' ||
              typeof colorAmounts[`comp${component.componentId}`] !== 'undefined'
            ? colorAmounts[component.componentId] || colorAmounts['comp' + component.componentId]
            : {};

      const componentTotalAmount = getComponentTotalAmount(componentValues, component.totalAmount);
      return acc + componentTotalAmount;
    }, 0);
  } else if (orderType === 'EAN_ORDER') {
    totalAmount = colorSizes.reduce((acc, sizeItem) => {
      const sizeValues =
        typeof colorAmounts === 'undefined'
          ? color.salesOrderItems?.[sizeItem.b24ProductVariantId]
          : typeof colorAmounts[sizeItem.b24ProductVariantId] !== 'undefined'
            ? colorAmounts[sizeItem.b24ProductVariantId]
            : {};
      const sizeTotalAmount = getSizeTotalAmount(sizeValues, batchSize);
      return acc + sizeTotalAmount;
    }, 0);
  } else if (orderType === 'BATCH_ORDER') {
    totalAmount = colorSizes.reduce((acc, sizeItem) => {
      const sizeValues =
        typeof colorAmounts === 'undefined'
          ? color.salesOrderItems?.[sizeItem.b24ProductVariantId]
          : typeof colorAmounts[sizeItem.b24ProductVariantId] !== 'undefined'
            ? colorAmounts[sizeItem.b24ProductVariantId]
            : {};
      const sizeTotalAmount = getBatchTotalAmount(sizeValues, batchSize);
      return acc + sizeTotalAmount;
    }, 0);
  }
  //console.log("getColorTotalAmount", totalAmount);
  return totalAmount;
}

export function getProductTotalAmount(
  product: RichSalesOrderProduct,
  productAmounts: SalesOrderProductAmounts | undefined
) {
  const orderType = product.orderType;
  const batchSize = parseInt(product.orderUnitAmount?.toString() || '0');
  const productTotalAmount = product.productColors.reduce((acc, color) => {
    const colorAmounts =
      typeof productAmounts === 'undefined'
        ? color.salesOrderItems
        : typeof productAmounts[color.assortmentProductColorId] !== 'undefined'
          ? productAmounts[color.assortmentProductColorId]
          : {};
    const colorTotalAmount = getColorTotalAmount(orderType, color, batchSize, colorAmounts);
    return acc + colorTotalAmount;
  }, 0);

  // console.log("getProductTotalAmount", productTotalAmount);
  return productTotalAmount;
}

export function getSalesOrderTotalAmount(
  salesOrder: SalesOrder & AdditionalRichData,
  amounts: SalesOrderAmounts
) {
  return salesOrder.products.reduce((acc, product) => {
    const productAmounts =
      typeof amounts === 'undefined'
        ? undefined
        : typeof amounts[product.assortmentProductId] !== 'undefined'
          ? amounts[product.assortmentProductId]
          : {};
    const productAmount = getProductTotalAmount(product, productAmounts);
    return acc + productAmount;
  }, 0);
}

export function getSalesOrderTotalPrice(
  salesOrder: SalesOrder & AdditionalRichData,
  amounts: SalesOrderAmounts
) {
  //console.log("getSalesOrderTotalPrice", salesOrder, amounts);
  return salesOrder.products.reduce((acc, product) => {
    const basePrice = product.wholesalesFinalPrice;
    const productAmounts =
      typeof amounts[product.assortmentProductId] !== 'undefined'
        ? amounts[product.assortmentProductId]
        : {};
    const totalAmount = getProductTotalAmount(product, productAmounts);
    const totalPrice = toNumber(multiply(totalAmount, basePrice));
    return toNumber(add(acc, totalPrice));
  }, 0.0);
}

export function getSalesOrderSummaryItemAmount(summaryAmounts: Record<string, number>) {
  if (!summaryAmounts) {
    return 0;
  }
  return Object.values(summaryAmounts).reduce((acc, item) => acc + item, 0);
}

export function getSalesOrderProductColorSummaryAmount(
  color: SalesOrderProductColor,
  orderType: SalesOrderProductItemOrderType | 'BATCH_ORDER' | '',
  summaryAmounts: SalesOrderSummaryAmounts,
  batchSize = 1
) {
  let batchAmount = parseInt(batchSize.toString());
  batchAmount = batchAmount > 0 ? batchAmount : 1;

  if (orderType === 'COMPONENT_ORDER') {
    return Object.values(color.components).reduce((total, component) => {
      const id = component.componentId;
      const componentSummaryAmounts = summaryAmounts['comp' + id] || {};
      const itemAmount = getSalesOrderSummaryItemAmount(componentSummaryAmounts);
      return total + itemAmount;
    }, 0);
  } else {
    return (
      color.colorSizes
        .filter((size) => !size.hidden)
        .reduce((total, size) => {
          const id = size.b24ProductVariantId;
          const sizeSummaryAmounts = summaryAmounts[id] || {};
          const itemAmount = getSalesOrderSummaryItemAmount(sizeSummaryAmounts);
          return total + itemAmount;
        }, 0) * batchAmount
    );
  }
}

export function getProductSummaryAmount(
  product: RichSalesOrderProduct,
  summaryAmounts: SalesOrderSummaryAmounts
) {
  const orderType = product.orderType;
  const batchAmount = product.isBatchProduct ? product.orderUnitAmount : 1;
  return product.productColors.reduce((acc, color) => {
    const colorTotalAmount = getSalesOrderProductColorSummaryAmount(
      color,
      orderType,
      summaryAmounts,
      batchAmount
    );
    return acc + colorTotalAmount;
  }, 0);
}

export function getSalesOrderSummaryTotalAmount(
  salesOrder: SalesOrder & AdditionalRichData,
  summaryAmounts: SalesOrderSummaryAmounts
) {
  return salesOrder.products.reduce((acc, product) => {
    const productAmount = getProductSummaryAmount(product, summaryAmounts);
    return acc + productAmount;
  }, 0);
}

export function getSalesOrderSummaryTotalPrice(
  salesOrder: SalesOrder & AdditionalRichData,
  summaryAmounts: SalesOrderSummaryAmounts
) {
  return salesOrder.products.reduce((acc, product) => {
    const basePrice = product.wholesalesFinalPrice;
    const totalAmount = getProductSummaryAmount(product, summaryAmounts);
    const totalPrice = toNumber(multiply(totalAmount, basePrice));
    return toNumber(add(acc, totalPrice));
  }, 0.0);
}

export function getVatPrice(price: number | string, vat: number | string, vatValue = defaultVat) {
  if (+String(vat).replace(',', '.') > 0) {
    return price || 0;
  }

  return toNumber(subtractVat(price || 0, vatValue));
}

export function getTotalPrice(totalAmount: number | string, price: number | string) {
  return toNumber(multiply(totalAmount, price));
}

function getItemDateAmount(
  salesOrderItems: SalesOrderItemAmounts | undefined,
  itemId: number | string,
  date: string
) {
  const dates = typeof salesOrderItems?.[itemId] !== 'undefined' ? salesOrderItems?.[itemId] : {};
  const dateObj = typeof dates[date] !== 'undefined' ? dates[date] : null;
  const amount = dateObj?.amount ? parseInt(dateObj.amount.toString()) : 0;

  return amount;
}

export function getSalesOrderCSVData(data: {
  productPageData: AnyAppData;
  showProducts: RichSalesOrderProduct[];
  assortment: SalesOrderAssortment;
  shop: SalesOrdersShop | undefined | { name: string };
  prices: { totalPriceVat0: string | number; totalPrice: string | number };
  summaryAmounts?: SalesOrderSummaryAmounts;
  chain?: string;
  /**
   * Include products with zero amounts
   *
   * @default false
   */
  includeZeroAmounts?: boolean;
}) {
  const showProducts = data.showProducts || [];
  const assortment = data.assortment || null;
  const productPageData = data.productPageData || null;
  const shop = data.shop || null;
  const prices = data.prices || {};
  const summaryAmounts = data.summaryAmounts || null;
  const chain = data.chain || 'Intersport';
  const includeZeroAmounts = data.includeZeroAmounts || false;

  if (
    !showProducts.length ||
    !assortment ||
    !isAppDataLoaded(productPageData, ['extranetUsersMapped'])
  ) {
    return [];
  }

  const UNIT_MAP = {
    PAA: 'PAR',
    PAC: 'PKT',
    PC: 'KPL',
    CAR: 'LTK',
    YES: 'PKT',
  };

  const { vendorsMapped, campaignsMapped_new } = productPageData;
  const assortmentVendor = vendorsMapped?.[assortment.vendorNumber] || null;
  const assortmentVendorId = assortmentVendor ? assortmentVendor.id : '';
  const assortmentVendorName = assortmentVendor
    ? assortmentVendor.name.replace(`${assortmentVendorId} - `, '')
    : '';

  // Extra loop to check if there is some batch products or components
  // And only then show batch / component fields
  let hasBatches = false;
  let hasComponents = false;
  for (const product of showProducts) {
    if (product.isBatchProduct) {
      hasBatches = true;
    } else if (product.orderType === 'COMPONENT_ORDER') {
      hasComponents = true;
    }

    if (hasBatches && hasComponents) {
      break;
    }
  }

  // Batch fields
  const batchProductFields = [];
  const variantBatchProductFields = [];
  if (hasBatches) {
    batchProductFields.push('salesBatchItemAmount', 'salesBatchAmount', 'salesBatchUnit');
    variantBatchProductFields.push('salesBatchItemAmount', 'salesBatchAmount', '' /* empty unit */);
  }

  // Component fields
  const componentProductFields = [];
  const variantComponentProductFields = [];
  if (hasComponents) {
    componentProductFields.push('componentItemAmount', 'componentAmount');
    variantComponentProductFields.push('componentItemAmount', '' /* empty component amount */);
  }

  const csvData: { data: Record<string, string | number>; fields: string[] }[] = [
    {
      /* Heading row */
      data: {
        /* Data for heading row */ buyer: 'Buyer',
        supplierCode: 'Supplier code',
        supplierName: 'Supplier name',
        brand: 'Brand',
        assortmentName: 'Assortment name',
        totalPriceVat0: 'Total price vat 0% €',
        totalPriceVat: 'Total price inc. vat €',
        productSapId: 'SAP ID',
        productId: 'Supplier ID',
        productName: 'Product name',
        unitPriceVat0: 'Unit price vat 0% €',
        unitPriceVat: 'Unit price inc. vat €',
        netPriceVat0: 'Net price vat 0% €',
        netPriceVat: 'Net price inc. vat €',
        b24Color: 'Supplier color',
        sapColor: 'SAP Color',
        modelNumber: 'Supplier model number', // productId + color code
        additionalInfo: 'Notes',
        campaigns: 'Campaigns',
        variantSapId: 'Variant SAP ID',
        variantSize: 'Variant size',
        ean: 'Variant EAN',
        deliveryDate: 'Delivery date',
        salesBatchItemAmount: 'Items in batch',
        salesBatchAmount: 'Batch amount',
        salesBatchUnit: 'Batch unit',
        componentItemAmount: 'Items in component',
        componentAmount: 'Component amount',
        itemsTotal: 'Items in total',
        totalUnitName: 'Unit',
        componentSapId: 'Component SAP ID',
      },
      fields: [
        /* Field order for heading row */ 'buyer',
        'supplierCode',
        'supplierName',
        'brand',
        'assortmentName',
        'totalPriceVat0',
        'totalPriceVat',
        'productId',
        'productSapId',
        'productName',
        'unitPriceVat0',
        'unitPriceVat',
        'netPriceVat0',
        'netPriceVat',
        'b24Color',
        'sapColor',
        'modelNumber',
        'campaigns',
        'additionalInfo',
        'deliveryDate',
        'variantSize',
        'ean',
        'variantSapId',
        ...batchProductFields,
        ...componentProductFields,
        'itemsTotal',
        'totalUnitName',
        'componentSapId',
      ],
    },
  ];

  /* Brand level */
  const brandFields = [
    'buyer',
    'supplierCode',
    'supplierName',
    'brand',
    'assortmentName',
    'totalPriceVat0',
    'totalPriceVat',
  ];

  csvData.push({
    data: {
      buyer: shop ? `${chain} ${shop.name}` : chain,
      supplierCode: assortmentVendorId,
      supplierName: assortmentVendorName,
      brand: showProducts[0]?.productBrand || '',
      assortmentName: `${assortment.longName} - ${assortment.additionalId}`,
      totalPriceVat0: prices.totalPriceVat0 || '',
      totalPriceVat: prices.totalPrice || '',
    },
    fields: brandFields,
  });

  const emptyBrandFields = brandFields.map((item) => '');
  const productFields = [
    ...emptyBrandFields,
    'productId',
    'productSapId',
    'productName',
    'unitPriceVat0',
    'unitPriceVat',
    'netPriceVat0',
    'netPriceVat',
  ];

  const emptyProductFields = productFields.map((item) => '');
  const colorFields = [
    ...emptyProductFields,
    'b24Color',
    'sapColor',
    'modelNumber',
    'campaigns',
    'additionalInfo',
  ];

  const emptyColorFields = colorFields.map((item) => '');
  const dateFields = [
    ...emptyColorFields,
    'deliveryDate',
    '', // size
    '', // ean
    '', // sap id
    ...batchProductFields,
    ...componentProductFields,
    'dateAmount',
    'totalUnitName',
    'componentSapId',
  ];

  const variantFields = [
    ...emptyColorFields,
    '' /* empty deliveryDate */,
    'variantSize',
    'ean',
    'variantSapId',
    ...variantBatchProductFields,
    ...variantComponentProductFields,
    'itemsTotal',
    '' /* empty unit name */,
    'componentSapId',
  ];

  for (const product of showProducts) {
    // Product level data
    const productData = {
      productName: `${product.productBrand} ${product.sapName || product.productName}`,
      productSapId: product.sapId,
      productId: product.productCode,
      amount: 0,
      unitPriceVat0: product.productPricingMapped.purchase.price,
      unitPriceVat: product.productPricingMapped.purchase.priceVat,
      netPriceVat0: product.productPricingMapped.net.price,
      netPriceVat: product.productPricingMapped.net.priceVat,
    };

    const isBatchProduct = !!product.isBatchProduct;
    const salesBatchItemAmount = isBatchProduct ? parseInt(product.orderUnitAmount.toString()) : 1;
    const isComponentProduct = product.orderType === 'COMPONENT_ORDER';
    const colorItems = []; // Store colors

    for (const color of product.productColors) {
      // Color level data
      const colorData = {
        b24Color: color.b24ColorName,
        sapColor: color.colorSapCode,
        modelNumber: `${color.productCode}-${color.b24ColorId}`,
        additionalInfo: color.additionalInfoPlainText ? color.additionalInfoPlainText : '',
        campaigns: color.campaigns.length
          ? '"' +
            color.campaigns
              .map(
                (campaign) =>
                  `${campaignsMapped_new?.[campaign.campaignUuid]?.name} - ${campaign.id}`
              )
              .join(' \n') +
            '"'
          : '',
        amount: 0,
      };

      const colorDateItems = []; // Store dates

      if (color.deliveryDates?.valuesArr) {
        for (const date of color.deliveryDates.valuesArr) {
          // Delivery date level data
          const dateData = {
            deliveryDate: `${date.slice(6, 8)}.${date.slice(4, 6)}.${date.slice(0, 4)}`,
            dateAmount: 0,
            salesBatchItemAmount: isBatchProduct ? salesBatchItemAmount : '',
            salesBatchAmount: isBatchProduct ? 0 : '',
            salesBatchUnit: isBatchProduct
              ? `${product.baseUnit} (${UNIT_MAP[product.baseUnit as keyof typeof UNIT_MAP]})`
              : '',
            componentItemAmount: '',
            componentAmount: '',
            componentSapId: '',
            totalUnitName: `${product.baseUnit} (${UNIT_MAP[product.baseUnit as keyof typeof UNIT_MAP]})`,
          };

          if (isComponentProduct) {
            // Component (lajitelma)
            for (const componentId of Object.keys(color.salesOrderItems ?? {})) {
              const compoKey = String(componentId).startsWith('comp')
                ? String(componentId).slice(4)
                : componentId;
              const component = color.components[compoKey];

              if (!component) {
                throw new Error(`Component ${compoKey} not found`);
              }

              let amount = 0;

              if (summaryAmounts) {
                // Summary from all shops
                const componentSummaryAmounts = summaryAmounts[componentId];
                const dateAmount = componentSummaryAmounts?.[date]
                  ? parseInt(componentSummaryAmounts[date].toString())
                  : 0;
                amount = dateAmount / (component?.totalAmount ?? 0);
              } else {
                // Single shop
                amount = getItemDateAmount(color.salesOrderItems, componentId, date);
              }

              if (amount > 0 || includeZeroAmounts) {
                // Component level
                const componentData = {
                  ...dateData,
                  componentItemAmount: 0,
                  componentAmount: amount,
                  dateAmount: component.totalAmount * amount,
                  componentSapId: component.componentSapId || '',
                };

                const variantData = []; // Store component variants
                for (const size of component.items) {
                  const sizeAmount = size.amount ? parseInt(size.amount.toString()) : 0;
                  if (sizeAmount > 0) {
                    componentData.componentItemAmount += sizeAmount;

                    variantData.push({
                      data: {
                        // eslint-disable-next-line quotes
                        ean: "'" + size.variantEan,
                        variantSize: size.variantSize,
                        variantSapId: size.variantSapId,
                        deliveryDate: dateData.deliveryDate,
                        salesBatchItemAmount: '',
                        salesBatchAmount: '',
                        salesBatchUnit: '',
                        componentItemAmount: sizeAmount,
                        itemsTotal: sizeAmount * amount,
                        totalUnitName: `${product.baseUnit} (${UNIT_MAP[product.baseUnit as keyof typeof UNIT_MAP]})`,
                      },
                      fields: variantFields,
                    });
                  }
                }

                // Add component date if variantData
                if (variantData.length) {
                  colorDateItems.push({
                    data: componentData,
                    fields: dateFields,
                  });
                  colorDateItems.push(...variantData);
                }
              }
            }
          } else {
            const variantData = []; // Store variants

            // EAN or Batch (myyntierä)
            const sizes =
              !color.colorSizes || (color.colorSizes && !color.colorSizes.length)
                ? []
                : color.colorSizes.filter((size) => !size.hidden);

            for (const size of sizes) {
              let sizeTotalAmount = 0;
              let amount = 0;
              if (summaryAmounts) {
                const sizeAmounts = summaryAmounts[size.b24ProductVariantId];
                amount = sizeAmounts?.[date] ? parseInt(sizeAmounts[date].toString()) : 0;
              } else {
                amount = getItemDateAmount(color.salesOrderItems, size.b24ProductVariantId, date);
              }

              if (amount > 0 || includeZeroAmounts) {
                sizeTotalAmount = amount;
                let batchAmount: string | number = '';
                if (isBatchProduct) {
                  // "Myyntierä"
                  //if (!summaryAmounts) {
                  sizeTotalAmount = amount * salesBatchItemAmount;
                  if (typeof dateData.salesBatchAmount === 'number') {
                    dateData.salesBatchAmount += amount;
                  }
                  batchAmount = amount;
                  /*} else {
                    // Summary contains the total item amount (id doesn't anymore??)
                    const currentBatchAmount = amount / salesBatchItemAmount;
                    dateData.salesBatchAmount += currentBatchAmount;
                    batchAmount = currentBatchAmount;
                  }*/
                }

                dateData.dateAmount += sizeTotalAmount;

                variantData.push({
                  data: {
                    // eslint-disable-next-line quotes
                    ean: "'" + size.ean,
                    variantSize: size.size,
                    variantSapId: size.sizeSapId,
                    deliveryDate: dateData.deliveryDate,
                    salesBatchItemAmount: dateData.salesBatchItemAmount,
                    salesBatchAmount: batchAmount,
                    salesBatchUnit: dateData.salesBatchUnit,
                    itemsTotal: sizeTotalAmount,
                    totalUnitName: `${product.baseUnit} (${UNIT_MAP[product.baseUnit as keyof typeof UNIT_MAP]})`,
                  },
                  fields: variantFields,
                });
              }
            }

            // Add date if it has variants
            if (variantData.length) {
              colorDateItems.push({
                data: dateData,
                fields: dateFields,
              });
              colorDateItems.push(...variantData);
            }
          }
        }
      }

      // Add color, if it has date items
      if (colorDateItems.length) {
        colorItems.push({
          data: colorData,
          fields: colorFields,
        });
        colorItems.push(...colorDateItems);
      }
    }

    // Add product, if it has color items
    if (colorItems.length) {
      csvData.push({
        data: productData,
        fields: productFields,
      });
      csvData.push(...colorItems);
    }
  }

  return csvData;
}
